import { useEffect, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Debounced auto-save. Serializes `value`; when it changes from the last saved
 * snapshot, waits `delay` ms of quiet, then calls `save`. Flushes on unmount so
 * leaving the page never loses an edit.
 */
export function useAutoSave<T>(value: T, save: (v: T) => Promise<unknown>, delay = 800): { status: SaveStatus; error: string | null } {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const lastSaved = useRef(JSON.stringify(value));
  const latest = useRef(value);
  const saveRef = useRef(save);
  const timer = useRef<number | null>(null);
  latest.current = value;
  saveRef.current = save;

  const flush = async () => {
    const snapshot = JSON.stringify(latest.current);
    if (snapshot === lastSaved.current) return;
    setStatus('saving');
    setError(null);
    try {
      await saveRef.current(latest.current);
      lastSaved.current = snapshot;
      setStatus('saved');
      window.setTimeout(() => setStatus((s) => (s === 'saved' ? 'idle' : s)), 1500);
    } catch (e: any) {
      setStatus('error');
      setError(e?.message ?? 'Could not save');
    }
  };

  useEffect(() => {
    if (JSON.stringify(value) === lastSaved.current) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, delay);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value), delay]);

  // Flush pending edits when the component goes away (tab switch, navigation).
  useEffect(() => () => { if (timer.current) { window.clearTimeout(timer.current); void flush(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { status, error };
}

export function SaveStatusDot({ status, error }: { status: SaveStatus; error: string | null }) {
  if (status === 'idle') return null;
  if (status === 'error') return <span className="error small">{error ?? 'Could not save'}</span>;
  return <span className={`muted small save-status ${status}`}>{status === 'saving' ? 'Saving…' : 'Saved ✓'}</span>;
}
